import React from "react";
import { isMobile } from "react-device-detect";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ICON } from "../../constants";
import { getSvgIcon } from "../../svgUtility";
import clsx from "clsx";
import data from "./sportsData.json";
import useStyles from "./SportsVenuePageStyles";
import Headers from "../Header";
import Footer from "../Footer";
import OpenAppCard from "../OpenApp/OpenAppCard";
import Navigation from "../Navigation/Navigation";
import WebCityFooter from "./WebCityFooter";
import MobileCityFooter from "./MobileCityFooter";

function parseSlug(slug = "") {
  // Expected pattern: "{sport}-courts-in-{city}"
  const parts = slug.split("-");

  // Check structure: [sport, "courts", "in", city]
  if (parts.length < 4) return { sport: "", city: "" };

  const sport = parts[0]; // "pickleball"
  const city = parts.slice(3).join("-"); // supports multi-word city like "new-delhi"

  return { sport, city };
}

const SportsVenuePage = () => {
  const { slug } = useParams(); // example: pickleball-courts-in-pune
  const { sport, city } = parseSlug(slug);
  const classes = useStyles();

  const cityData = data[city]?.[sport];

  if (!cityData) return <div>Data not found for this city.</div>;

  const {
    seo,
    heading,
    subHeading,
    subHeadingPara,
    secondSubHeading,
    secondSubHeadingPara,
    venues,
    faqs,
  } = cityData;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo?.description} />
        <meta name="keywords" content={seo?.keywords} />
        <link rel="canonical" href={window?.location?.href} />
        <meta property="og:title" content={seo?.title} />
        <meta property="og:description" content={seo?.description} />
      </Helmet>

      <Headers />

      <main className={classes.sportsContainer}>
        {/* Header Section */}
        <header>
          <h1 className={classes.sportsHeading}>{heading}</h1>
          <h2 className={classes.sportsSubheading}>{subHeading}</h2>
          <p className={classes.sportsPara}>{subHeadingPara}</p>
        </header>

        {/* Secondary Section */}
        <section>
          <h3 className={classes.sportsSectionHeading}>{secondSubHeading}</h3>
          <p className={classes.sportsSectionPara}>{secondSubHeadingPara}</p>
        </section>

        {/* Venue Grid Section */}
        <section className={classes.venueGrid}>
          {venues?.map((venue) => (
            <a
              key={venue.id}
              href={venue.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.venueCard}
            >
              <img
                src={venue?.image}
                alt={venue?.name}
                className={classes.venueImage}
                loading="lazy"
              />
              <div className={classes.venueInfo}>
                <h4 className={classes.venueName}>{venue?.name}</h4>
                <div>
                  <div className={clsx(classes.flex, classes.alignCenter)}>
                    <img
                      src="https://pages.khelomore.com/lp/km-sixer-tournament/images/time.svg"
                      alt="time icon"
                      className={classes.timeIcon}
                    />
                    <span className={classes.price}>{venue?.time}</span>
                  </div>
                  <div>
                    <span className={classes.price}>{venue?.price}</span>
                  </div>
                  <div className={clsx(classes.flex, classes.alignBase)}>
                    <img
                      src="https://pages.khelomore.com/lp/km-sixer-tournament/images/location.svg"
                      alt="location icon"
                      className={classes.timeIcon}
                    />
                    <address className={classes.price}>
                      {venue?.address}
                    </address>
                  </div>
                </div>
              </div>
              <div className={classes.forwardArrow}>
                {getSvgIcon(ICON.FORWARD_ARROW_ICON)}
              </div>
            </a>
          ))}
        </section>

        {/* FAQ Section */}
        <section className={classes.faqSection}>
          <h3 className={classes.faqTitle}>Frequently Asked Questions</h3>
          <ul className={classes.faqList}>
            {faqs?.map((faq, index) => (
              <li key={index} className={classes.faqItem}>
                <h4 className={classes.faqQuestion}>
                  {index + 1}. {faq?.question}
                </h4>
                <p className={classes.faqAnswer}>{faq?.answer}</p>
              </li>
            ))}
          </ul>
        </section>
      </main>
      {isMobile ? <MobileCityFooter /> : <WebCityFooter />}
      {isMobile && <OpenAppCard />}
      {isMobile && <Navigation />}
    </>
  );
};

export default SportsVenuePage;
